import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box } from '@mui/material'
import createStyles from '@mui/styles/createStyles'
import makeStyles from '@mui/styles/makeStyles'
import { ItemDetails } from 'components'
import { OverviewSection } from './OverviewSection'
import DeleteAzureApplicationRight from './DeleteAzureApplicationRight'
import { Skeleton } from '@mui/material'
import ApplicationAssignments from './AppRightsDetailsDrawer/Assignments'
import AppRightsPeople from './AppRightsDetailsDrawer/People'
import useSubcomponents from 'useSubcomponents'
import {
    defaultDataProtectedSubcomponent,
    getAccessibleTabs,
    RESOURCE_TYPE_NAMES,
    updateUrlQueryParams,
} from 'utils'
import ApplicationAppRightsEligbility from './AppRightsDetailsDrawer/Eligibility'
import ApplicationAppRightsSensitiveFunctions from './AppRightsDetailsDrawer/SensitiveFunctions'
import { PageContextProvider } from 'pageContext'
import { useQuery } from 'packages/core'
import { useHistory } from 'react-router'
import ApplicationAppRightsFieldTypeValues from './AppRightsDetailsDrawer/FieldTypeValues'
import ApplicationAppRightsFieldTypes from './AppRightsDetailsDrawer/FieldTypes'
import { useAppState } from 'appContext'

const useStyles = makeStyles(() =>
    createStyles({
        deleteBtn: {
            padding: '40px 32px 45px',
            '& div': {
                marginLeft: '0',
                gap: '10px',
            },
            '& button': {
                width: '132px !important',
            },
        },
        itemTabMenuBar: {
            padding: '0 30px',
        },
    }),
)

const AppRightDetails = ({
    role,
    allowAppRoleDirectAssignment,
    applicationType,
    resourceId,
    resourceSystemModuleID,
}) => {
    const { t } = useTranslation()
    const classes = useStyles()
    const query = useQuery()
    const history = useHistory()
    const { hasAccessToControl, canDeleteApplicationAppRight } =
        useSubcomponents()
    const [, dispatch] = useAppState()

    const APP_RIGHTS_TABS = [
        {
            value: 'assignments',
            label: 'Assignments',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationAppRightAssignmentsTab-Control',
            },
            display: allowAppRoleDirectAssignment,
        },
        {
            value: 'people',
            label: 'People',
            requireAccess: {
                control: 'ResourceAdmin-ApplicationAppRightPeopleTab-Control',
            },
            display: allowAppRoleDirectAssignment,
        },
        {
            value: 'eligibility',
            label: 'Eligibility',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationAppRightEligibilityTab-Control',
            },
            display: true,
        },
        {
            value: 'sensitiveFunctions',
            label: 'SensitiveFunctions',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationAppRightSensativeFunctionsTab-Control',
            },
            display: true,
        },
        {
            value: 'fieldTypes',
            label: 'FieldTypes',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationAppRightFieldTypesTab-Control',
            },
            display: true,
        },
        {
            value: 'fieldTypeValues',
            label: 'FieldTypeValues',
            requireAccess: {
                control:
                    'ResourceAdmin-ApplicationAppRightFieldTypeValuesTab-Control',
            },
            display: true,
        },
    ]
    useEffect(() => {
        if (role && role?.id) {
            dispatch({
                type: 'SET_CURRENT_RESOURCE',
                payload: {
                    targetAssigneeId: role?.id,
                    targetAssigneeFriendlyName:
                        role?.friendlyName ?? t(RESOURCE_TYPE_NAMES.LOCALRIGHT),
                    targetAssignee_ResourceTypeFriendlyName: t(
                        RESOURCE_TYPE_NAMES.LOCALRIGHT,
                    ),
                },
            })

            dispatch({
                type: 'SET_TARGET_RESOURCE',
                payload: {
                    targetAssigneeId: role?.id,
                    targetAssigneeFriendlyName:
                        role?.friendlyName ?? t(RESOURCE_TYPE_NAMES.LOCALRIGHT),
                    targetAssignee_ResourceTypeFriendlyName: t(
                        RESOURCE_TYPE_NAMES.LOCALRIGHT,
                    ),
                },
            })
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [role, role?.id])
    const [allAccessibleTabs, setAllAccessibleTabs] = useState([])
    const [activeTab, setActiveTab] = useState('')

    useEffect(() => {
        if (resourceId !== null && resourceId !== undefined) {
            const activeTabInfo = getAccessibleTabs(
                query,
                history,
                APP_RIGHTS_TABS,
                query.get('drawerActiveTab'),
                null,
                hasAccessToControl,
                'drawerActiveTab',
                '',
            )
            setAllAccessibleTabs(activeTabInfo.allAccessibleTabs)
            setActiveTab(activeTabInfo.activeTab)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceId])

    const handleTabChange = (tab) => {
        setActiveTab(tab)
        updateUrlQueryParams(
            query,
            history,
            [{ key: 'drawerActiveTab', value: tab }],
            [],
        )
    }

    return (
        <Fragment>
            {/* Overview section start*/}
            {!role ? (
                <Box>
                    <Skeleton
                        variant="rectangular"
                        animation="wave"
                        height={8}
                        style={{ marginTop: 8 }}
                    />
                </Box>
            ) : (
                <Box>
                    <Box>
                        <OverviewSection
                            role={role}
                            applicationType={applicationType}
                        />
                    </Box>
                    {canDeleteApplicationAppRight && (
                        <Box
                            data-protectedsubcomponent={
                                canDeleteApplicationAppRight
                            }
                            className={classes.deleteBtn}
                        >
                            <DeleteAzureApplicationRight
                                item={role}
                                drawerView={true}
                            />
                        </Box>
                    )}
                </Box>
            )}
            {/* Overview section end*/}

            {/* App rights tabs start */}
            <div style={{ height: '100%' }}>
                <ItemDetails.Tabs
                    variant="standard"
                    value={activeTab}
                    onChange={(_, value) => handleTabChange(value)}
                    className={classes.itemTabMenuBar}
                >
                    {allAccessibleTabs.map((item, index) => (
                        <ItemDetails.Tabs.Tab
                            key={`rights-tab-${index}`}
                            value={item.value}
                            label={t(item.label)}
                            data-protectedsubcomponent={
                                item?.requireAccess?.control ||
                                defaultDataProtectedSubcomponent
                            }
                        />
                    ))}
                </ItemDetails.Tabs>

                <Box
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '90%',
                        justifyContent: 'space-between',
                    }}
                >
                    <Box>
                        {activeTab === 'assignments' && (
                            <PageContextProvider key="RequestViewAppRightsDetailsAssignments">
                                <ApplicationAssignments
                                    role={role}
                                    roleId={role.id}
                                    resourceSystemModuleID={
                                        resourceSystemModuleID
                                    }
                                />
                            </PageContextProvider>
                        )}
                        {activeTab === 'eligibility' && (
                            <PageContextProvider key="RequestViewAppRightsDetailsEligibility">
                                <ApplicationAppRightsEligbility
                                    resourceTypeId={role.resourceTypeId}
                                    roleId={role.id}
                                    resourceId={role.resourceId}
                                    applicationType={applicationType}
                                />
                            </PageContextProvider>
                        )}
                        {activeTab === 'sensitiveFunctions' && (
                            <PageContextProvider key="RequestViewAppRightsDetailsSensitiveFunctions">
                                <ApplicationAppRightsSensitiveFunctions
                                    roleId={role.id}
                                />
                            </PageContextProvider>
                        )}
                        {activeTab === 'people' && (
                            <PageContextProvider key="RequestViewAppRightsDetailsPeople">
                                <AppRightsPeople
                                    roleId={role.id}
                                    applicationType={applicationType}
                                    resourceId={resourceId}
                                    resourceSystemModuleID={
                                        resourceSystemModuleID
                                    }
                                />
                            </PageContextProvider>
                        )}
                        {activeTab === 'fieldTypes' && (
                            <PageContextProvider key="RequestViewAppRightsDetailsFieldTypes">
                                <ApplicationAppRightsFieldTypes
                                    roleId={role?.id}
                                    globalRightId={role?.globalRightId}
                                    resourceId={role?.resourceId}
                                />
                            </PageContextProvider>
                        )}

                        {activeTab === 'fieldTypeValues' && (
                            <PageContextProvider key="RequestViewAppRightsDetailsFieldTypeValues">
                                <ApplicationAppRightsFieldTypeValues
                                    roleId={role.id}
                                    applicationType={applicationType}
                                    resourceId={resourceId}
                                    role={role}
                                />
                            </PageContextProvider>
                        )}
                    </Box>
                </Box>
            </div>
            {/* App rights tabs end */}
        </Fragment>
    )
}

export default AppRightDetails
